import { IColumnHeaderFilterProps } from 'devextreme-react/data-grid';
import { ColumnProps } from './DataGridProps';
import { get } from 'lodash';
import { ApolloClient } from '@apollo/client';
import { isType } from '#/common/utils/typeGuards';
import { createDataSource } from '#/common/utils/createDataSource';
import { isUUID } from '#/common/utils/patterns/uuid';

export const getColumnHeaderFilter = <T>(
  gqlClient: ApolloClient<object>,
  column: ColumnProps<T>,
): IColumnHeaderFilterProps => {
  const { headerFilter, dataField, lookup } = column;
  if (!headerFilter) {
    if (!lookup) {
      return {};
    }
    const { dataSource, displayExpr = 'text', valueExpr = 'value' } = lookup;
    return {
      dataSource: isType(dataSource, 'array')
        ? dataSource.map((item) => ({
            text: get(item, displayExpr, ''),
            value: get(item, valueExpr, ''),
          }))
        : dataSource,
    };
  }

  if (!('store' in headerFilter) && !('getItemList' in headerFilter)) {
    return headerFilter;
  }

  const map = (item: any) => {
    const displayValue = get(item, headerFilter.displayExpr, '');
    const valueExpr = headerFilter.valueExpr ?? headerFilter.displayExpr;
    const value = get(item, valueExpr, '');

    return {
      ...item,
      text: displayValue,
      value: headerFilter.filterType === 'contains' ? [dataField, 'contains', value] : value,
    };
  };

  if ('store' in headerFilter) {
    return {
      dataSource: {
        store: headerFilter.store,
        map,
      },
    };
  }

  if ('getItemList' in headerFilter) {
    return {
      dataSource: {
        store: createDataSource<any>(gqlClient, {
          ...headerFilter,
          cacheEnabled: true,
          searchKey: headerFilter.displayExpr,
        }),
        map,
      },
    };
  }

  return headerFilter;
};

export const getElementPosition = (el?: HTMLDivElement | null): DOMRect => {
  if (el) {
    const rect = el.getBoundingClientRect();
    return rect;
  }

  return { bottom: 0, left: 0, top: 0, right: 0, x: 0, y: 0 } as DOMRect;
};

/**
 * WARNING: Use this function only with DATE values, not with DATETIME!
 *
 * DevExtreme tries to convert both date and datetime values to UTC.
 * This conversion works for datetime values, but we store date values in UTC and use them only in UTC.
 * We cannot stop this auto-conversion, but where needed we can edit the date, to overcome this limitation.
 *
 * This function edits the date: it removes the timezone offset from its value.
 * So, when DX converts to UTC we will still get the correct date value.
 *
 * If the date value already has time 00:00:00.000 in UTC: we do not apply any conversion.
 * In this case, the fix is already applied.
 *
 * Example:
 * 1. Having date: 23-Dec-2025 with local TZ EET (+2:00 from UTC).
 * 2. Without the fix: DevExtreme will convert this to: 22-Dec-2025 22:00 (removing the +2:00 from UTC).
 * 3. When we use it as date, we will get 22-Dec-2025, which is incorrect.
 *
 * Applying the fix between step 1 and 2, we will set the date to: 23-Dec-2025 2:00 (EET).
 * On step 2, DX will convert it to: 23-Dec-2025 00:00 (UTC).
 * On step 3, we will use the correct date 23-Dec-2025.
 */
export const fixDateTZ = (date: Date) => {
  const isStartOfDay = date.getUTCHours() === 0 && date.getUTCMinutes() === 0;
  if (isStartOfDay) {
    return date;
  }

  date.setTime(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
  return date;
};

// Filter Expression Helpers

type FilterExpression = unknown[] | undefined;
type DataType = 'string' | 'number' | 'date' | 'boolean' | 'object' | 'datetime' | 'uuid';

const isValidDateInput = (val: unknown): val is string | Date | number => {
  if (val === null || val === undefined || val === '') return false;
  if (val instanceof Date) return !Number.isNaN(val.getTime());
  if (typeof val === 'string' && val.trim() === '') return false;
  return true;
};

const createValidDate = (val: unknown): Date | null => {
  if (!isValidDateInput(val)) return null;
  const date = new Date(val);
  return Number.isNaN(date.getTime()) ? null : date;
};

const hasValue = (val: unknown): boolean => val !== null && val !== undefined && val !== '';

const buildRangeFilter = (
  field: string,
  startVal: unknown,
  endVal: unknown,
  startOp: string = '>=',
  endOp: string = '<=',
  connector: 'and' | 'or' = 'and',
): FilterExpression => {
  const hasStart = hasValue(startVal);
  const hasEnd = hasValue(endVal);

  if (!hasStart && !hasEnd) return undefined;
  if (hasStart && hasEnd) {
    return [[field, startOp, startVal], connector, [field, endOp, endVal]];
  }
  if (hasStart) return [field, startOp, startVal];
  return [field, endOp, endVal];
};

const buildDateBetweenFilter = (
  field: string,
  startValue: unknown,
  endValue: unknown,
  dataType: DataType,
): FilterExpression => {
  const startDate = createValidDate(startValue);
  const endDate = createValidDate(endValue);

  if (!startDate && !endDate) return undefined;

  if (dataType === 'date') {
    if (startDate) fixDateTZ(startDate);
    if (endDate) fixDateTZ(endDate);
  }

  // Adjust end date to include the entire day for datetime
  if (dataType === 'datetime' && endDate) {
    const isStartOfDay =
      endDate.getHours() === 0 && endDate.getMinutes() === 0 && endDate.getSeconds() === 0;
    if (isStartOfDay) {
      endDate.setHours(23, 59, 59, 999);
    }
  }

  const startISO = startDate?.toISOString() ?? null;
  const endISO = endDate?.toISOString() ?? null;

  return buildRangeFilter(field, startISO, endISO);
};

const buildNumericBetweenFilter = (
  field: string,
  startValue: unknown,
  endValue: unknown,
): FilterExpression => {
  return buildRangeFilter(field, startValue, endValue);
};

const adjustDateRange = (date: Date, end: Date, dataType: DataType): void => {
  if (dataType === 'date') {
    end.setUTCDate(date.getUTCDate() + 1);
  } else {
    date.setUTCMinutes(date.getUTCMinutes() - 1);
    end.setUTCMinutes(end.getUTCMinutes() + 1);
  }
};

const buildDateEqualsFilter = (field: string, date: Date, dataType: DataType): FilterExpression => {
  const end = new Date(date);
  adjustDateRange(date, end, dataType);
  return [[field, '>=', date.toISOString()], 'and', [field, '<', end.toISOString()]];
};

const buildDateNotEqualsFilter = (
  field: string,
  date: Date,
  dataType: DataType,
): FilterExpression => {
  const end = new Date(date);
  adjustDateRange(date, end, dataType);
  return [[field, '<', date.toISOString()], 'or', [field, '>=', end.toISOString()]];
};

const handleBetweenFilter = (
  field: string,
  value: unknown,
  dataType: DataType | undefined,
): FilterExpression => {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const [startValue, endValue] = value;
  const isDateType = dataType === 'date' || dataType === 'datetime';

  return isDateType
    ? buildDateBetweenFilter(field, startValue, endValue, dataType)
    : buildNumericBetweenFilter(field, startValue, endValue);
};

const handleDateFilter = (
  field: string,
  value: unknown,
  operation: string,
  dataType: DataType,
  defaultHandler: (v: unknown, op: string, t: unknown) => FilterExpression,
): FilterExpression => {
  const date = new Date(value as string);
  if (Number.isNaN(date.getTime())) {
    return defaultHandler(value, operation, value);
  }

  if (dataType === 'date') {
    fixDateTZ(date);
  }

  if (operation === '=') return buildDateEqualsFilter(field, date, dataType);
  if (operation === '<>') return buildDateNotEqualsFilter(field, date, dataType);
  return [field, operation, date.toISOString()];
};

const handleFilterField = (
  filterField: string,
  value: unknown,
  operation: string,
): FilterExpression | null => {
  if (!isUUID(String(value))) {
    const textOperation = operation === '=' ? 'contains' : operation;
    return [filterField, textOperation, value];
  }
  return null;
};

export interface FilterExpressionParams {
  dataField: string;
  dataType?: DataType;
  filterField?: string;
  value: unknown;
  operation: string;
  defaultHandler: (value: unknown, operation: string, target: unknown) => FilterExpression;
}

export const calculateFilterExpression = (params: FilterExpressionParams): FilterExpression => {
  const { dataField, dataType, filterField, value, operation, defaultHandler } = params;
  const isDateType = dataType === 'date' || dataType === 'datetime';

  if (operation === 'between') {
    return handleBetweenFilter(dataField, value, dataType);
  }

  if (isDateType) {
    return handleDateFilter(dataField, value, operation, dataType, defaultHandler);
  }

  if (filterField) {
    const result = handleFilterField(filterField, value, operation);
    if (result) return result;
  }

  return defaultHandler(value, operation, value);
};

export function createColumnFilterExpression(
  dataField: string,
  dataType?: DataType,
  filterField?: string,
) {
  return function (this: any, value: unknown, operation: string) {
    return calculateFilterExpression({
      dataField,
      dataType,
      filterField,
      value,
      operation,
      defaultHandler: (v, op, t) => this.defaultCalculateFilterExpression(v, op, t),
    });
  };
}
